/**
 * Size-guide drawer behavior:
 *  - Trigger click → opens dialog-component
 *  - Trigger relocation into variant-picker's Size legend (once, on DOM ready)
 *  - Mobile-only: scroll-position indicator bar under each table
 *  - Desktop-only: prev/next chevron buttons beside the unit toggle
 *
 * Mobile vs desktop logic is forked at init so neither viewport pays
 * for the other viewport's behavior.
 */

const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

function relocateTrigger(root) {
  const trigger = root.querySelector('[data-size-guide-trigger]');
  if (!trigger) return;

  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    root.showDialog?.();
  });

  const place = () => {
    const legends = document.querySelectorAll('fieldset legend');
    for (const lg of legends) {
      const txt = (lg.textContent || '').trim().toLowerCase();
      if (txt === 'size' || txt.startsWith('size ') || txt.startsWith('size\n')) {
        lg.style.display = 'flex';
        lg.style.justifyContent = 'space-between';
        lg.style.alignItems = 'baseline';
        lg.style.width = '100%';
        lg.appendChild(trigger);
        break;
      }
    }
    trigger.classList.remove('invisible');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', place, { once: true });
  } else {
    place();
  }
}

function wireMobileScrollHints(root) {
  const updaters = [];
  root.querySelectorAll('.size-guide__table').forEach((tbl) => {
    const wrap = tbl.querySelector('.size-guide__table-wrap');
    const hint = tbl.querySelector('.size-guide__scroll-hint');
    const thumb = hint?.querySelector('.size-guide__scroll-hint-thumb');
    if (!wrap || !hint || !thumb) return;

    const update = () => {
      const max = wrap.scrollWidth - wrap.clientWidth;
      if (max <= 1) {
        hint.style.display = 'none';
        return;
      }
      hint.style.display = '';
      const thumbPct = (wrap.clientWidth / wrap.scrollWidth) * 100;
      const progress = wrap.scrollLeft / max;
      thumb.style.width = thumbPct + '%';
      thumb.style.left = (progress * (100 - thumbPct)) + '%';
    };

    wrap.addEventListener('scroll', update, { passive: true });
    updaters.push(update);
  });
  return updaters;
}

function wireDesktopArrows(root) {
  const prevBtn = root.querySelector('[data-size-guide-scroll="prev"]');
  const nextBtn = root.querySelector('[data-size-guide-scroll="next"]');
  const wraps = Array.from(root.querySelectorAll('.size-guide__table-wrap'));
  if (!prevBtn || !nextBtn || wraps.length === 0) return [];

  const visibleWrap = () => wraps.find((w) => w.offsetParent !== null) || wraps[0];

  const sync = () => {
    const wrap = visibleWrap();
    if (!wrap) return;
    const max = wrap.scrollWidth - wrap.clientWidth;
    if (max <= 1) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }
    prevBtn.disabled = wrap.scrollLeft <= 0;
    nextBtn.disabled = wrap.scrollLeft >= max - 1;
  };

  const scrollStep = (dir) => {
    const wrap = visibleWrap();
    if (!wrap) return;
    const max = wrap.scrollWidth - wrap.clientWidth;
    const step = Math.max(wrap.clientWidth * 0.6, 160);
    let target = wrap.scrollLeft + dir * step;
    // Snap to boundary if one step would leave less than half a step remaining,
    // so the final click always lands exactly at 0 or max (instead of "almost").
    if (dir > 0 && target > max - step / 2) target = max;
    if (dir < 0 && target < step / 2) target = 0;
    wrap.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
  };

  prevBtn.addEventListener('click', () => scrollStep(-1));
  nextBtn.addEventListener('click', () => scrollStep(1));
  wraps.forEach((w) => w.addEventListener('scroll', sync, { passive: true }));
  return [sync];
}

function initDialog(root) {
  if (root.dataset.sgWired) return;
  root.dataset.sgWired = '1';

  relocateTrigger(root);

  const updaters = MOBILE_MQ.matches
    ? wireMobileScrollHints(root)
    : wireDesktopArrows(root);

  if (updaters.length === 0) return;

  const updateAll = () => updaters.forEach((fn) => fn());
  window.addEventListener('resize', updateAll);
  root.addEventListener('dialog:open', () => requestAnimationFrame(updateAll));
  root.querySelector('.size-guide__unit-input')?.addEventListener('change', () => {
    requestAnimationFrame(updateAll);
  });
}

document.querySelectorAll('dialog-component.size-guide').forEach(initDialog);
